import { defineField, defineType } from 'sanity'
import { articleContent } from '../../data/richText'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import { PageBlockTypes } from '../pageBlocks'
import VehicleInput from '../../components/VehicleInput'

export default defineType({
  title: 'Article',
  name: 'article',
  type: 'document',
  orderings: [
    {
      title: 'Title A-Z',
      name: 'titleAsc',
      by: [{field: 'title', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  fields: [
    getPageInfo({ path: 'article' }),
    defineField({
      title: 'Title',
      name: 'title',
      type: 'string',
      group: 'content',
    }),
    defineField({
      title: 'Slug',
      name: 'slug',
      type: 'slug',
      options: {
        source: 'title',
      },
      group: 'content',
    }),
    defineField({
      name: 'publishedAt',
      title: 'Published at',
      type: 'datetime',
      options: {
        dateFormat: 'DD.MM.YYYY',
        timeFormat: 'HH:mm',
      },
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'author',
      title: 'Author',
      type: 'reference',
      to: [
        {
          type: 'author',
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'mainImage',
      title: 'Main Image',
      type: 'image',
      options: {
        hotspot: true,
      },
      group: 'content',
    }),
    defineField({
      name: 'featured',
      title: 'Featured',
      description: 'Featured articles appear at the top of blog listings',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'teaser',
      title: 'Teaser',
      description: 'Lead text shown on article listing',
      type: 'text',
      validation: (rule) => rule.max(200),
      group: 'content',
    }),
    defineField({
      name: 'content',
      title: 'Content',
      type: 'array',
      of: articleContent,
      group: 'content',
    }),
    defineField({
      name: 'categories',
      title: 'Categories',
      type: 'array',
      of: [
        {
          name: 'category',
          title: 'Category',
          type: 'reference',
          to: [
            {
              type: 'articleCategory',
            },
          ],
        },
      ],
      validation: (rule) => rule.min(1).required(),
      group: 'content',
    }),
    defineField({
      title: 'Use max width text',
      description:
        'Check this if you want to make text from page blocks same width as the rest of the content',
      name: 'maxWidthText',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      title: 'Page blocks',
      name: 'pageBlocks',
      type: 'array',
      of: [...PageBlockTypes],
      group: 'content',
    }),
    defineField({
      name: 'tags',
      title: 'Tags',
      description: 'Related wheels and vehicles',
      type: 'array',
      of: [
        {
          name: 'boltPattern',
          title: 'Bolt pattern',
          type: 'reference',
          to: [
            {
              type: 'boltPattern',
            },
          ],
        },
        {
          name: 'fitmentType',
          title: 'Fitment type',
          type: 'reference',
          to: [
            {
              type: 'fitment',
            },
          ],
        },
        {
          name: 'vehicle',
          title: 'Vehicle',
          type: 'object',
          fields: [
            defineField({
              name: 'allMakes',
              title: 'Show On All Makes',
              type: 'boolean',
              initialValue: true,
            }),
            defineField({
              name: 'vehicle',
              title: 'Vehicle',
              type: 'object',
              hidden: ({ parent }) => parent?.allMakes,
              fields: [
                defineField({
                  name: 'make',
                  type: 'reference',
                  to: [{ type: 'make' }],
                }),
                defineField({
                  name: 'model',
                  type: 'reference',
                  to: [{ type: 'model' }],
                }),
                defineField({
                  name: 'generation',
                  type: 'reference',
                  to: [{ type: 'generation' }],
                }),
                defineField({
                  name: 'submodel',
                  type: 'reference',
                  to: [{ type: 'submodel' }],
                }),
              ],
              components: {
                input: VehicleInput,
              },
            }),
          ],
          preview: {
            select: {
              make: 'vehicle.make.name',
              model: 'vehicle.model.name',
              generation: 'vehicle.generation.name',
              allMakes: 'allMakes',
            },
            prepare({ make, model, generation, allMakes }) {
              return {
                title: allMakes
                  ? 'All vehicles'
                  : make
                  ? `${make} ${model ? `- ${model}` : ''} ${
                      generation ? `- ${generation}` : ''
                    }`
                  : 'Untitled',
                subtitle: 'Vehicle',
              }
            },
          },
        },
        {
          name: 'wheelDesign',
          title: 'Wheel design',
          type: 'reference',
          to: [
            {
              type: 'design',
            },
          ],
        },
        {
          name: 'wheelDiameter',
          title: 'Wheel diameter',
          type: 'reference',
          to: [
            {
              type: 'diameter',
            },
          ],
        },
        {
          name: 'wheelLine',
          title: 'Wheel line',
          type: 'reference',
          to: [
            {
              type: 'line',
            },
          ],
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'showOnBlog',
      title: 'Show On Blog Main Page',
      type: 'boolean',
      initialValue: true,
      group: 'content',
    }),
    defineField({
      name: 'showInResourceCenter',
      title: 'Show in Resource Center',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'showOnGenerationPage',
      title: 'Show On Generation Page',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'showOnWheelLandingPage',
      title: 'Show On Wheel Landing Page',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'showOnFitmentGuide',
      title: 'Show On Fitment Guide',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'showOnHomePage',
      title: 'Show On Homepage',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'showOnRealPerformancePage',
      title: 'Show On Real Performance Page',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
  ],
  preview: {
    select: {
      name: 'pageInfo.title',
      media: 'mainImage',
    },
    prepare({ name: title = 'New page', media }) {
      return { title, subtitle: 'Article', media }
    },
  },
})